import { useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { mirrorEmailVerifiedToUserDoc, notifyEmailVerified } from '../utils/registrationFlow';

/**
 * Poll Firebase Auth for emailVerified while `enabled`.
 * Also checks on window focus / tab visible. Calls onVerified once when true.
 */
export function useEmailVerificationPoll({ enabled, onVerified, intervalMs = 2500 }) {
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      firedRef.current = false;
      return undefined;
    }

    let cancelled = false;
    firedRef.current = false;

    const check = async () => {
      if (cancelled || firedRef.current) return;
      const auth = getAuth();
      let user = auth.currentUser;
      if (!user) return;

      try {
        await user.reload();
        await user.getIdToken(true);
      } catch {
        return;
      }
      if (cancelled || firedRef.current) return;

      user = auth.currentUser;
      if (!user?.emailVerified) return;

      firedRef.current = true;
      try {
        await mirrorEmailVerifiedToUserDoc(user);
      } catch {
        /* non-blocking */
      }
      notifyEmailVerified({ source: 'poll' });
      if (!cancelled) onVerifiedRef.current?.(user);
    };

    check();
    const id = setInterval(check, intervalMs);

    const onFocus = () => {
      check();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs]);
}
